import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'
import { canWrite, canWriteJournal } from '../lib/supabase'
import { awardPostCI } from '../lib/useColumnInches'
import RichEditor from '../components/RichEditor'
import ImageUpload from '../components/ImageUpload'
import PublishCelebration from '../components/PublishCelebration'
import { pingIndexNow } from '../lib/indexnow'
import { ga } from '../lib/analytics'

const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSei7zcQ7kRy7yK-FrBC5EqgaFD1zuYiQJWLsxdwIJFNTnki7w/viewform'
const AUTOSAVE_KEY = 'margins_draft_autosave'
const AUTOSAVE_INTERVAL = 30000 // 30s

export default function WritePage() {
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')           // post ID if editing
  const promptId = searchParams.get('prompt')        // prompt ID if responding to prompt
  const responseToId = searchParams.get('response')  // post ID if writing a response piece
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [focusMode, setFocusMode] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false) // mobile sidebar drawer
  const [coldOpen, setColdOpen] = useState(false)    // anonymous publish mode
  const [shareToken, setShareToken] = useState(null)
  const [generatingToken, setGeneratingToken] = useState(false)

  const saveOrSubmitRef = useRef(null)

  // Esc exits focus mode, Cmd+Enter publishes
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape' && focusMode) setFocusMode(false)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        saveOrSubmitRef.current?.('published')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [focusMode])
  const [submitting, setSubmitting] = useState(false)
  const [celebration, setCelebration] = useState(null) // { title, ciEarned }
  const [lastSaved, setLastSaved] = useState(null)
  const [form, setForm] = useState(null)

  // Reset form when editId changes (fixes stale closure on navigation)
  useEffect(() => {
    setForm(null)
  }, [editId])

  // Reset form whenever editId changes so navigating to edit a different post works
  useEffect(() => {
    setForm(null)
  }, [editId])

  // Init form once auth is settled and profile is available
  useEffect(() => {
    if (authLoading || !profile || form) return

    // Edit mode: load existing post
    if (editId) {
      supabase.from('posts').select('*').eq('id', editId).single().then(({ data, error }) => {
        if (error || !data) { toast('Could not load post.', 'error'); navigate('/dashboard'); return }
        if (data.author_id !== user.id) { toast('You can only edit your own posts.', 'error'); navigate('/dashboard'); return }
        setForm({
          title: data.title || '',
          section: data.section || 'culture',
          tag: data.tag || '',
          excerpt: data.excerpt || '',
          body: data.body || '',
          featured: data.featured || false,
          images: [data.cover_image, data.image_2].filter(Boolean),
        })
      })
      return
    }

    // Try to restore autosave
    const saved = localStorage.getItem(AUTOSAVE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setForm({ images: [], ...parsed, cover_image: undefined, image_2: undefined })
        setLastSaved(new Date(parsed._savedAt))
        toast('Draft restored from autosave.', 'success')
        return
      } catch {}
    }
    setForm({
      title: '', section: 'culture',
      tag: '', excerpt: '', body: '', featured: false, images: []
    })
  }, [authLoading, profile, form])

  // Keep a ref to latest form so autosave interval doesn't re-create on every keystroke
  const formRef = useRef(form)
  useEffect(() => { formRef.current = form }, [form])

  // Autosave every 30s — stable interval, reads from ref
  useEffect(() => {
    if (!user) return
    const interval = setInterval(() => {
      const f = formRef.current
      if (!f) return
      if (f.title?.trim() || f.body?.replace(/<[^>]*>/g, '').trim()) {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ ...f, _savedAt: new Date().toISOString() }))
        setLastSaved(new Date())
      }
    }, AUTOSAVE_INTERVAL)
    return () => clearInterval(interval)
  }, [user])

  // Wait for auth to resolve before making any role decisions
  if (authLoading) return (
    <div className={`write-page ${focusMode ? "write-focus-mode" : ""}`}>
      <div style={styles.restricted}>
        <p style={{ fontFamily: "'DM Mono', monospace", color: 'var(--muted)', fontSize: '0.75rem' }}>Loading...</p>
      </div>
    </div>
  )

  if (!user) return (
    <div className={`write-page ${focusMode ? "write-focus-mode" : ""}`}><div style={styles.restricted}>
      <h2 style={styles.restrictedTitle}>Sign In Required</h2>
      <p style={styles.restrictedText}>You need to be signed in to write for The Margins.</p>
      <button className="btn btn-ink" onClick={() => navigate('/auth')}>Sign In</button>
    </div></div>
  )

  if (!profile || !form) return (
    <div className={`write-page ${focusMode ? "write-focus-mode" : ""}`}><div style={styles.restricted}>
      <p style={{ fontFamily: "'DM Mono', monospace", color: 'var(--muted)', fontSize: '0.75rem' }}>Loading...</p>
    </div></div>
  )

  if (!canWrite(profile.role)) return (
    <div className={`write-page ${focusMode ? "write-focus-mode" : ""}`}><div style={styles.restricted}>
      <h2 style={styles.restrictedTitle}>Writers Only</h2>
      <p style={styles.restrictedText}>sign up and start writing for the feed immediately.</p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <a href={FORM_URL} target="_blank" rel="noreferrer" className="btn btn-accent">the journal (coming soon)</a>
        <button className="btn btn-ink" onClick={() => navigate('/auth')}>Sign In / Sign Up</button>
      </div>
    </div></div>
  )

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function clearAutosave() {
    localStorage.removeItem(AUTOSAVE_KEY)
  }

  async function generateShareLink() {
    if (!form.title.trim()) { toast('Add a title first.', 'error'); return }
    setGeneratingToken(true)
    // Save as draft first if not saved
    const token = Math.random().toString(36).slice(2) + Date.now().toString(36)
    const payload = {
      title: form.title.trim(),
      section: form.section,
      tag: form.tag.trim(),
      excerpt: form.excerpt.trim(),
      body: form.body.trim(),
      read_time: autoReadTime,
      cover_image: (form.images || [])[0] || null,
      image_2: (form.images || [])[1] || null,
      author_id: user.id,
      status: 'draft',
      share_token: token,
    }
    const { error } = await supabase.from('posts').upsert(payload, { onConflict: 'share_token', ignoreDuplicates: false })
    if (error) { toast(error.message, 'error'); setGeneratingToken(false); return }
    const link = `${window.location.origin}/preview/${token}`
    navigator.clipboard.writeText(link)
    setShareToken(link)
    toast('Draft link copied to clipboard.', 'success')
    setGeneratingToken(false)
  }

  async function saveOrSubmit(status) {
    const bodyText = form.body.replace(/<[^>]*>/g, '').trim()
    if (!form.title.trim() || !bodyText) {
      toast('Please fill in a title and body.', 'error'); return
    }
    const fn = status === 'draft' ? setSaving : setSubmitting
    fn(true)

    const images = (form.images || []).filter(Boolean)
    const payload = {
      title: form.title.trim(),
      section: form.section,
      tag: form.tag.trim(),
      excerpt: form.excerpt.trim(),
      body: form.body.trim(),
      read_time: autoReadTime,
      featured: form.featured,
      cover_image: images[0] || null,
      image_2: images[1] || null,
      status
    }

    // Edit mode: update existing post
    if (editId) {
      const { error } = await supabase.from('posts').update(payload).eq('id', editId)
      if (error) {
        toast(error.message || 'Something went wrong.', 'error')
        fn(false); return
      }
      fn(false)
      toast('Post updated.', 'success')
      navigate(`/post/${editId}`)
      return
    }

    // New post
    const { data: newPost, error } = await supabase.from('posts').insert({
      ...payload,
      author_id: coldOpen ? null : user.id,
      is_anonymous: coldOpen,
      claimed_by: null,
      prompt_id: promptId ? parseInt(promptId) : null,
      response_to: responseToId ? parseInt(responseToId) : null,
    }).select().single()
    if (error) {
      if (error.code === '42501' || (error.message && error.message.toLowerCase().includes('policy'))) {
        toast('Permission error — ask an admin to run SUPABASE_RUN_THIS.sql', 'error')
      } else {
        toast(error.message || 'Something went wrong. Try again.', 'error')
      }
      fn(false)
      return
    }

    // Award CI immediately on publish
    if (status === 'published' && newPost && user?.id) {
      awardPostCI(user.id, newPost).catch(console.error)
      pingIndexNow(newPost.id).catch(console.error)
      ga.publishPost(newPost.id, autoReadTime)
    }

    clearAutosave()
    fn(false)

    if (status === 'published') {
      const bodyWords = (form.body || '').replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length
      const readTime = Math.max(1, Math.ceil(bodyWords / 200))
      const ci = 5 + readTime * 2 // base 5 + 2 per read-minute
      setCelebration({ title: form.title.trim(), ciEarned: ci })
    } else {
      toast('Draft saved.', 'success')
      navigate('/dashboard')
    }
  }

  const wordCount = form.body.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length
  const autoReadTime = Math.max(1, Math.ceil(wordCount / 200))

  // Keep ref current so keyboard shortcut always calls latest version
  saveOrSubmitRef.current = saveOrSubmit

  return (
    <div className={`write-page ${focusMode ? 'write-focus-mode' : ''}`}>
      {celebration && (
        <PublishCelebration
          title={celebration.title}
          ciEarned={celebration.ciEarned}
          onDone={() => { setCelebration(null); navigate('/feed') }}
        />
      )}

      {/* Header bar */}
      <div className="write-header">
        <div className="write-header-left">
          <span className="write-header-eyebrow">{editId ? 'Editing' : 'New Piece'}</span>
          {lastSaved && !editId && (
            <span className="write-autosave-note">autosaved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>
        <div className="write-header-actions">
          <button className="btn btn-ghost write-focus-btn" onClick={() => setFocusMode(f => !f)} title="Focus mode (Esc to exit)">
            {focusMode ? '⊠' : '⊡'}
          </button>
          {!editId && (
            <button className="btn btn-ghost" onClick={generateShareLink} disabled={generatingToken} title="Share draft link">
              {generatingToken ? '...' : '⤴'}
            </button>
          )}
          {editId ? (
            <>
              <button className="btn btn-ghost" onClick={() => navigate(`/post/${editId}`)}>Cancel</button>
              <button className="btn btn-accent" onClick={() => saveOrSubmit('published')} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save →'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => saveOrSubmit('draft')} disabled={saving}>
                {saving ? '...' : 'Draft'}
              </button>
              <button className="btn btn-accent" onClick={() => saveOrSubmit('published')} disabled={submitting} title="Ctrl/⌘ + Enter">
                {submitting ? '...' : 'Publish →'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Two-column layout: editor left, metadata right */}
      <div className="write-layout">

        {/* Mobile sidebar toggle */}
        <button
          className="write-sidebar-toggle"
          onClick={() => setSidebarOpen(o => !o)}
          title="Post settings"
        >
          ⚙ Settings
        </button>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main editor column */}
        <div className="write-editor-col">
          {/* Feature hints — shown once, subtle */}
          <div style={{
            display: 'flex', gap: '1.5rem', flexWrap: 'wrap',
            marginBottom: '1.75rem', padding: '0.75rem 1rem',
            background: 'rgba(200,64,26,0.05)', border: '1px solid rgba(200,64,26,0.15)',
            borderRadius: 8, fontSize: '0.68rem', fontFamily: "'DM Mono', monospace",
            color: 'var(--muted)', letterSpacing: '0.04em',
          }}>
            <span>✎ <strong style={{ color: 'var(--ink)' }}>Select text</strong> for quick formatting</span>
            <span>⌘ <strong style={{ color: 'var(--ink)' }}>Ctrl/⌘ + Enter</strong> to publish</span>
            <span>◐ <strong style={{ color: 'var(--ink)' }}>Auto-saved</strong> every 30 seconds</span>
            <span>⊡ <strong style={{ color: 'var(--ink)' }}>Focus mode</strong> — distraction-free writing</span>
          </div>
          <div className="form-group write-title-group">
            <input className="form-input" value={form.title} onChange={e => update('title', e.target.value)}
              placeholder="A title that earns the argument..." />
          </div>

          <div className="form-group write-body-group">
            <RichEditor
              value={form.body}
              onChange={v => update('body', v)}
              placeholder="Write your piece here. Back it up. Mean it."
            />
          </div>

          <div className="write-form-actions">
            {editId ? (
              <>
                <button className="btn btn-ghost" onClick={() => navigate(`/post/${editId}`)}>Cancel</button>
                <button className="btn btn-accent" onClick={() => saveOrSubmit('published')} disabled={submitting}>
                  {submitting ? 'Saving…' : 'Save Changes →'}
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-ghost" onClick={() => saveOrSubmit('draft')} disabled={saving}>
                  {saving ? 'Saving...' : 'Save as Draft'}
                </button>
                <button className="btn btn-accent" onClick={() => saveOrSubmit('published')} disabled={submitting}>
                  {submitting ? 'Publishing...' : 'Publish to the Feed →'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Sidebar metadata column */}
        <aside className={`write-sidebar ${sidebarOpen ? 'write-sidebar-open' : ''}`}>
          {/* Prompt context banner */}
          {promptId && (
            <div style={{ background: 'rgba(200,64,26,0.06)', border: '1px solid rgba(200,64,26,0.2)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '0' }}>
              <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.54rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.3rem' }}>Responding to prompt</p>
              <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', color: 'var(--ink)', lineHeight: 1.5, margin: 0 }}>Your piece will appear under the prompt on the Prompts page.</p>
            </div>
          )}
          {responseToId && (
            <div style={{ background: 'rgba(200,64,26,0.06)', border: '1px solid rgba(200,64,26,0.2)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '0' }}>
              <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.54rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.3rem' }}>Argument thread</p>
              <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', color: 'var(--ink)', lineHeight: 1.5, margin: 0 }}>Your response will be linked to the original piece.</p>
            </div>
          )}
          <div className="write-sidebar-section">
            <p className="write-sidebar-label">Posting to</p>
            <div style={styles.sideValue}>
              <span style={{ color: 'var(--accent)', marginRight: 6 }}>◆</span>
              The Feed
            </div>
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.5rem', color: 'var(--muted)', marginTop: 4, letterSpacing: '0.06em' }}>
              your piece goes live on the feed after publish
            </p>
          </div>

          {/* Cold Open toggle */}
          {!editId && (
            <div className="write-sidebar-section" style={{ background: coldOpen ? 'rgba(200,64,26,0.06)' : 'transparent', border: coldOpen ? '1px solid rgba(200,64,26,0.2)' : '1px solid transparent', borderRadius: 8, padding: '0.85rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <p style={{ ...styles.sideSectionLabel, margin: 0 }}>Cold Open</p>
                <button
                  type="button"
                  onClick={() => setColdOpen(c => !c)}
                  style={{ background: coldOpen ? 'var(--accent)' : 'var(--rule)', border: 'none', borderRadius: 12, width: 36, height: 20, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                  title="Publish anonymously — claim later"
                >
                  <span style={{ position: 'absolute', top: 2, left: coldOpen ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </button>
              </div>
              <p className="write-sidebar-hint">
                {coldOpen
                  ? 'Publishing anonymously. Your name won\'t appear. You can claim this piece later from your dashboard.'
                  : 'Publish anonymously — let the writing stand on its own. You can claim it later.'}
              </p>
            </div>
          )}

          <div className="write-sidebar-section">
            <label className="form-label" style={{ marginBottom: '0.25rem', display: 'block' }}>Tag / Category</label>
            <p className="write-sidebar-hint">Groups your piece with similar writing on the feed. e.g. Essay, Politics, Film</p>
            <input className="form-input" value={form.tag} onChange={e => update('tag', e.target.value)}
              placeholder="Essay, Film, Politics…" style={{ fontSize: '0.85rem', marginTop: '0.4rem' }} />
          </div>

          <div className="write-sidebar-section">
            <label className="form-label" style={{ marginBottom: '0.25rem', display: 'block' }}>Excerpt</label>
            <p className="write-sidebar-hint">The teaser shown on feed cards. If blank, we'll pull the opening lines. Make it count.</p>
            <textarea className="form-textarea" value={form.excerpt} onChange={e => update('excerpt', e.target.value)}
              placeholder="A line that makes someone want to read it."
              style={{ minHeight: 72, fontSize: '0.85rem', resize: 'vertical', marginTop: '0.4rem' }} />
          </div>

          <div className="write-sidebar-section">
            <p className="write-sidebar-label">Read Time</p>
            <div style={{ ...styles.sideValue, color: 'var(--accent)' }}>~{autoReadTime} min</div>
            <p className="write-sidebar-hint">Calculated from your word count. Updates as you write.</p>
          </div>

          {/* Images — up to 10, optional */}
          <div className="write-sidebar-section" style={{ paddingBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <p style={{ ...styles.sideSectionLabel, marginBottom: 0 }}>Images</p>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.48rem', color: 'var(--muted)', letterSpacing: '0.06em' }}>
                {(form.images || []).filter(Boolean).length}/10
              </span>
            </div>
            <p className="write-sidebar-hint" style={{ marginBottom: '0.6rem' }}>First image becomes your cover — shown on feed cards and at the top of your piece.</p>
            {(form.images || []).filter(Boolean).length === 0 ? (
              <ImageUpload
                value={null}
                onChange={v => { if (v) update('images', [v]) }}
                label="Add cover image"
                hint="optional · jpg, png, webp"
              />
            ) : (
              <>
                {(form.images || []).filter(Boolean).map((url, i) => (
                  <div key={i} style={{ position: 'relative', marginBottom: '0.6rem' }}>
                    <img src={url} alt={`Image ${i+1}`} style={{ width: '100%', maxHeight: 120, objectFit: 'cover', display: 'block', borderRadius: 6, border: '1px solid var(--rule)' }} />
                    <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.44rem', background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 6px', letterSpacing: '0.08em', borderRadius: 3 }}>
                        {i === 0 ? 'cover' : `#${i+1}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => { const imgs = (form.images || []).filter(Boolean); imgs.splice(i, 1); update('images', imgs) }}
                        style={{ background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', width: 22, height: 22, fontSize: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}
                        title="Remove"
                      >✕</button>
                    </div>
                  </div>
                ))}
                {(form.images || []).filter(Boolean).length < 10 && (
                  <ImageUpload
                    value={null}
                    onChange={v => { if (v) { const imgs = [...(form.images || []).filter(Boolean), v]; update('images', imgs) } }}
                    label={`Add image ${(form.images || []).filter(Boolean).length + 1}`}
                    hint=""
                  />
                )}
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh', background: 'var(--cream)' },
  restricted: { maxWidth: 500, margin: '8rem auto', textAlign: 'center', padding: '0 2rem' },
  restrictedTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '2rem', fontWeight: 700, marginBottom: '1rem' },
  restrictedText: { color: 'var(--muted)', lineHeight: 1.75, marginBottom: '2rem' },
  header: { padding: '2.5rem 3rem', borderBottom: '1px solid var(--rule)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--paper)' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.8rem', fontWeight: 700 },
  autosaveNote: { fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', marginTop: '0.4rem', letterSpacing: '0.06em' },
  actions: { display: 'flex', gap: '0.75rem' },
  form: { maxWidth: 800, margin: '0 auto', padding: '3rem 2rem' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  formActions: { display: 'flex', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--rule)', marginTop: '1rem' },
  sideSection: {
    paddingBottom: '0.25rem',
    marginBottom: '0',
  },
  sideSectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.54rem',
    letterSpacing: '0.16em',
    color: 'var(--accent)',
    marginBottom: '0.4rem',
    textTransform: 'uppercase',
  },
  sideHint: {
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: '0.72rem',
    color: 'var(--muted)',
    lineHeight: 1.55,
    marginBottom: '0.1rem',
  },
  sideValue: {
    fontFamily: "'Space Grotesk', sans-serif",
    fontWeight: 700,
    fontSize: '1rem',
    color: 'var(--ink)',
  },
}
